import React, { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { isNaN, useFormik } from 'formik';
import * as Yup from 'yup';
import InputMask from 'react-input-mask';
import toast from 'react-hot-toast';
import PuffLoader from 'react-spinners/PuffLoader';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { MdCreditCard, MdOutlinePix, MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import { BiBarcode } from 'react-icons/bi';
import { CheckoutWrapper, FormCard, FormContainer, LoadingContainer, NotFoundContainer } from './styles';
import Form, { FormGroup, Input, Select } from '../ui/form';
import { Col, Row } from '../ui/Wrapper';
import Label from '../ui/form/label';
import FieldError from '../FieldError';
import SubmitButton from '../SubmitButton/SubmitButton';
import Button from '../ui/button';
import Heading from '../ui/Heading';
import Text from '../ui/Text';
import { theme } from '../../theme';
import invoicesService from '../../services/invoicesService';

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string().required('Número do cartão obrigatório'),
  holderName: Yup.string().required('Nome impresso obrigatório'),
  expMonth: Yup.string().required('Mês obrigatório'),
  expYear: Yup.string().required('Ano obrigatório'),
  cvv: Yup.string().required('CVV obrigatório'),
  installments: Yup.string().required('Quantidade de parcelas é obrigatória'),
});
const multiCardValidationSchema = Yup.object().shape({
  card1Number: Yup.string().required('Número do cartão 1 obrigatório'),
  card1HolderName: Yup.string().required('Nome impresso no cartão 1 obrigatório'),
  card1ExpMonth: Yup.string().required('Mês no cartão 1 obrigatório'),
  card1ExpYear: Yup.string().required('Ano no cartão 1 obrigatório'),
  card1Cvv: Yup.string().required('CVV no cartão 1 obrigatório'),
  card1Amount: Yup.number().required('Valor no cartão 1 obrigatório'),
  card1Installments: Yup.string().required('Quantidade de parcelas no cartão 1 é obrigatória'),
  card2Number: Yup.string().required('Número do cartão 2 obrigatório'),
  card2HolderName: Yup.string().required('Nome impresso no cartão 2 obrigatório'),
  card2ExpMonth: Yup.string().required('Mês no cartão 2 obrigatório'),
  card2ExpYear: Yup.string().required('Ano no cartão 2 obrigatório'),
  card2Cvv: Yup.string().required('CVV no cartão 2 obrigatório'),
  card2Amount: Yup.number().required('Valor no cartão 2 obrigatório'),
  card2Installments: Yup.string().required('Quantidade de parcelas no cartão 2 é obrigatória'),
});

const options = {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};
const formatNumber = new Intl.NumberFormat('pt-BR', options);

const pagarmeAppId = process.env.GATSBY_PAGARME_API_KEY;

export default function CheckoutPagarme() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [paymentPlans, setPaymentPlans] = useState([]); // lista de parcelas
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [pixInfo, setPixInfo] = useState(null);
  const [boletoInfo, setBoletoInfo] = useState(null);
  const [order, setOrder] = useState({
    items: [],
    total: 0,
  });
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');

  const [card1Amount, setCard1Amount] = useState('');
  const [card2Amount, setCard2Amount] = useState('');

  useEffect(() => {
    const pathOrderId = window.location.pathname.split('/').pop();

    if (!pathOrderId) {
      setError('ID do pedido informado é inválido.');
      return;
    }

    setOrderId(pathOrderId);
  }, []);

  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      setIsLoadingOrder(true);
      try {
        const { data } = await invoicesService.getInvoiceByIntegrationId(orderId, 'reCaptchaToken');
        setOrder(data);
        setPaymentMethod(data.payment_method_default);
        setAllowedPaymentMethods(data.payment_methods);
        setPaymentPlans(data.payment_plan_credit_card || []);
        if (data.transaction) {
          const trx = data.transaction;

          if (trx.transaction_type === 'pix') {
            setPaymentMethod('pix');
            setPixInfo({
              qrCode: trx.content_qrcode,
              copyPaste: trx.content_line,
              expiresAt: trx.expires_at,
            });
            setPaymentStatus('success');
            setPaymentMessage('Pagamento via PIX gerado');
          }

          if (trx.transaction_type === 'boleto') {
            setPaymentMethod('boleto');
            setBoletoInfo({
              pdfUrl: trx.content_pdf,
              barcode: trx.content_barcode,
              qrCode: trx.content_qrcode,
              copyPaste: trx.content_line,
            });
            setPaymentStatus('success');
            setPaymentMessage('Boleto Gerado');
          }
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'ID do pedido inválido.');
      } finally {
        setIsLoadingOrder(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const handlePixPayment = async values => {
    setLoading(true);
    setPaymentStatus(null);
    setPaymentMessage('');
    try {
      if (paymentMethod !== 'pix') {
        throw new Error('Método de pagamento inválido para PIX');
      }

      const body = {
        total_amount: order.total_amount,
        due_date: order.due_date,
        payment_method: paymentMethod,
        integration_id: order.integration_id,
        contract_id: order.student_contract_id,
        school_unit_id: order.school_unit_id,
      };

      const reCaptchaToken = await executeRecaptcha('checkout_form');
      const { data } = await invoicesService.addPaymentMethod(body, reCaptchaToken);

      setPixInfo({
        qrCode: data.content_qrcode,
        copyPaste: data.content_line,
        expiresAt: data.expires_at,
      });
      setPaymentStatus('success');
      setPaymentMessage('Pagamento via PIX gerado');
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Erro ao gerar pagamento via PIX. Contate o suporte.';
      setPaymentStatus('error');
      setPaymentMessage(`Erro ao processar pagamento: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBoletoPayment = async values => {
    setLoading(true);
    setPaymentStatus(null);
    setPaymentMessage('');
    try {
      if (paymentMethod !== 'boleto') {
        throw new Error('Método de pagamento inválido para Boleto');
      }

      const body = {
        total_amount: order.total_amount,
        due_date: order.due_date,
        payment_method: paymentMethod,
        integration_id: order.integration_id,
        contract_id: order.student_contract_id,
        school_unit_id: order.school_unit_id,
      };

      const reCaptchaToken = await executeRecaptcha('checkout_form');
      const { data } = await invoicesService.addPaymentMethod(body, reCaptchaToken);

      setBoletoInfo({
        pdfUrl: data.content_pdf,
        barcode: data.content_barcode,
        qrCode: data.content_qrcode,
        copyPaste: data.content_line,
      });
      setPaymentStatus('success');
      setPaymentMessage('Boleto Gerado');
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Erro ao gerar pagamento via Boleto. Contate o suporte.';
      setPaymentStatus('error');
      setPaymentMessage(`Erro ao processar pagamento: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Submissão do cartão
  const onSubmitCreditCard = async values => {
    setLoading(true);
    setPaymentStatus(null);
    setPaymentMessage('');
    try {
      if (paymentMethod !== 'credit_card') {
        throw new Error('Método de pagamento inválido para Cartão de Crédito');
      }

      let body = {
        total_amount: order.total_amount,
        due_date: order.due_date,
        payment_method: paymentMethod,
        integration_id: order.integration_id,
        contract_id: order.student_contract_id,
        school_unit_id: order.school_unit_id,
      };

      const tokenResponse = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${pagarmeAppId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: values.cardNumber.replace(/\s/g, ''),
            holder_name: values.holderName,
            exp_month: Number(values.expMonth),
            exp_year: Number(values.expYear),
            cvv: values.cvv,
          },
        }),
      });

      const tokenJson = await tokenResponse.json();
      if (!tokenJson.id) {
        throw new Error(
          'Parece que os dados do cartão estão incorretos. Por favor, verifique o número, validade (mês/ano) e CVV e tente novamente.'
        );
      }

      body = {
        ...body,
        cards: [
          {
            card_installments: Number(values.installments),
            card_token: tokenJson.id,
            card_amount: order.total_amount,
          },
        ],
      };

      const reCaptchaToken = await executeRecaptcha('checkout_form');
      const { data } = await invoicesService.addPaymentMethod(body, reCaptchaToken);

      if (data.status === 'paid') {
        setPaymentStatus('success');
        setPaymentMessage('Pagamento efetuado com sucesso');
        toast.success('Pagamento realizado com sucesso!');
      } else {
        setPaymentStatus('error');
        setPaymentMessage(`Status do pagamento: ${data.status}`);
        toast.error(`Status: ${data.status}`);
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Erro ao gerar pagamento via Cartão de Crédito. Contate o suporte. ';
      setPaymentStatus('error');
      setPaymentMessage(`Erro ao processar pagamento: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitMultiCreditCard = async values => {
    setLoading(true);
    setPaymentStatus(null);
    setPaymentMessage('');

    try {
      if (paymentMethod !== 'multi_credit_card') {
        throw new Error('Método de pagamento inválido para Multi Cartão');
      }

      // 🔹 Gera tokens dos dois cartões
      const token1Response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${pagarmeAppId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: values.card1Number.replace(/\s/g, ''),
            holder_name: values.card1HolderName,
            exp_month: Number(values.card1ExpMonth),
            exp_year: Number(values.card1ExpYear),
            cvv: values.card1Cvv,
          },
        }),
      });

      const token2Response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${pagarmeAppId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: values.card2Number.replace(/\s/g, ''),
            holder_name: values.card2HolderName,
            exp_month: Number(values.card2ExpMonth),
            exp_year: Number(values.card2ExpYear),
            cvv: values.card2Cvv,
          },
        }),
      });

      const token1 = await token1Response.json();
      const token2 = await token2Response.json();

      if (!token1.id || !token2.id) throw new Error('Erro ao gerar tokens dos cartões');

      // 🔹 Monta corpo da requisição
      const body = {
        total_amount: order.total_amount,
        due_date: order.due_date,
        payment_method: paymentMethod,
        integration_id: order.integration_id,
        contract_id: order.student_contract_id,
        school_unit_id: order.school_unit_id,
        cards: [
          {
            card_installments: Number(values.card1Installments),
            card_token: token1.id,
            card_amount: values.card1Amount,
          },
          {
            card_installments: Number(values.card2Installments),
            card_token: token2.id,
            card_amount: Number(values.card2Amount),
          },
        ],
      };

      const reCaptchaToken = await executeRecaptcha('checkout_form');
      const { data } = await invoicesService.addPaymentMethod(body, reCaptchaToken);

      if (data.status === 'paid') {
        setPaymentStatus('success');
        setPaymentMessage('Pagamento com 2 cartões realizado com sucesso');
        toast.success('Pagamento aprovado!');
      } else {
        setPaymentStatus('error');
        setPaymentMessage(`Status do pagamento: ${data.status}`);
        toast.error(`Status: ${data.status}`);
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message || 'Erro ao gerar pagamento via Cartão de Crédito. Contate o suporte.';
      setPaymentStatus('error');
      setPaymentMessage(`Erro ao processar pagamento: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formikCreditCard = useFormik({
    initialValues: {
      cardNumber: '',
      holderName: '',
      expMonth: '',
      expYear: '',
      cvv: '',
      installments: '',
    },
    validationSchema,
    onSubmit: onSubmitCreditCard,
  });

  const formikMultiCreditCard = useFormik({
    initialValues: {
      card1Number: '',
      card1HolderName: '',
      card1ExpMonth: '',
      card1ExpYear: '',
      card1Cvv: '',
      card1Amount: '',
      card1Installments: '',
      card2Number: '',
      card2HolderName: '',
      card2ExpMonth: '',
      card2ExpYear: '',
      card2Cvv: '',
      card2Amount: '',
      card2Installments: '',
    },
    validationSchema: multiCardValidationSchema,
    onSubmit: onSubmitMultiCreditCard,
  });

  useEffect(() => {
    formikCreditCard.setFieldValue('installments', '');
    formikMultiCreditCard.setFieldValue('card1Installments', '');
    formikMultiCreditCard.setFieldValue('card2Installments', '');
    formikMultiCreditCard.setFieldValue('card1Amount', '');
    formikMultiCreditCard.setFieldValue('card2Amount', '');
  }, [paymentMethod]);

  if (error) {
    return (
      <NotFoundContainer>
        <Heading mb="24px">Ops!</Heading>
        <Text fontSize="lg" mb="24px">
          {error}
        </Text>
        <Button to="/">Voltar à página principal</Button>
      </NotFoundContainer>
    );
  }
  return (
    <CheckoutWrapper>
      <Row gutter="16px">
        {isLoadingOrder && (
          <LoadingContainer>
            <PuffLoader color={theme.colors.primary} size={60} />
          </LoadingContainer>
        )}
        {!isLoadingOrder && order?.invoices?.length > 0 && (
          <>
            <Col lg={8} md={12} sm={12}>
              {/* Row para os botões */}
              {!paymentStatus && (
                <Row gutter="8px" style={{ marginBottom: '20px' }}>
                  {allowedPaymentMethods.includes('credit_card') && (
                    <Col lg={12 / allowedPaymentMethods.length} md={12} sm={12}>
                      <Button
                        fullwidth
                        icon={<MdCreditCard />}
                        iconposition="left"
                        size="xsmall"
                        skin={paymentMethod === 'credit_card' ? 'primary' : 'primaryLight'}
                        onClick={() => setPaymentMethod('credit_card')}
                      >
                        Cartão
                      </Button>
                    </Col>
                  )}
                  {allowedPaymentMethods.includes('multi_credit_card') && (
                    <Col lg={12 / allowedPaymentMethods.length} md={12} sm={12}>
                      <Button
                        fullwidth
                        icon={<MdCreditCard />}
                        iconposition="left"
                        size="xsmall"
                        skin={paymentMethod === 'multi_credit_card' ? 'primary' : 'primaryLight'}
                        onClick={() => setPaymentMethod('multi_credit_card')}
                      >
                        2 Cartões
                      </Button>
                    </Col>
                  )}
                  {allowedPaymentMethods.includes('pix') && (
                    <Col lg={12 / allowedPaymentMethods.length} md={12} sm={12}>
                      <Button
                        fullwidth
                        icon={<MdOutlinePix />}
                        iconposition="left"
                        size="xsmall"
                        skin={paymentMethod === 'pix' ? 'primary' : 'primaryLight'}
                        onClick={() => setPaymentMethod('pix')}
                      >
                        PIX
                      </Button>
                    </Col>
                  )}
                  {allowedPaymentMethods.includes('boleto') && (
                    <Col lg={12 / allowedPaymentMethods.length} md={12} sm={12}>
                      <Button
                        fullwidth
                        icon={<BiBarcode />}
                        iconposition="left"
                        size="xsmall"
                        skin={paymentMethod === 'boleto' ? 'primary' : 'primaryLight'}
                        onClick={() => setPaymentMethod('boleto')}
                      >
                        Boleto
                      </Button>
                    </Col>
                  )}
                </Row>
              )}
              {/* Mensagem de sucesso ou erro */}
              {paymentStatus && (
                <Row gutter="16px">
                  <Col lg={12} md={12} sm={12}>
                    <FormCard>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '250px',
                          textAlign: 'center',
                        }}
                      >
                        {paymentStatus === 'success' ? (
                          <>
                            <MdCheckCircle size={80} color="green" />
                            <h4 style={{ marginTop: '20px' }}>{paymentMessage}</h4>

                            {paymentMethod === 'pix' && pixInfo && (
                              <Row>
                                <Col lg={12} style={{ textAlign: 'center', padding: '16px' }}>
                                  {pixInfo.qrCode && (
                                    <>
                                      <p>Pague com o QR Code abaixo:</p>
                                      <img
                                        src={pixInfo.qrCode}
                                        alt="QR Code PIX"
                                        style={{
                                          margin: '16px auto',
                                          width: '200px',
                                          height: '200px',
                                          objectFit: 'contain',
                                          display: 'block',
                                        }}
                                      />
                                      <p>
                                        <strong>Expira em:</strong>{' '}
                                        {new Date(pixInfo.expiresAt).toLocaleString('pt-BR', {
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                        })}
                                      </p>
                                    </>
                                  )}

                                  {/* Divisor visual */}
                                  {pixInfo.qrCode && pixInfo.copyPaste && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '24px 0',
                                      }}
                                    >
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                      <span style={{ margin: '0 10px', color: '#888' }}>ou</span>
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                    </div>
                                  )}

                                  {/* Copia e cola PIX */}
                                  {pixInfo.copyPaste && (
                                    <>
                                      <p>Ou pague copiando o código abaixo:</p>
                                      <div
                                        style={{
                                          display: 'block',
                                          background: '#f9f9f9',
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          margin: '12px auto',
                                          wordBreak: 'break-all',
                                          maxWidth: '90%',
                                        }}
                                      >
                                        {pixInfo.copyPaste}
                                      </div>
                                    </>
                                  )}
                                </Col>
                              </Row>
                            )}
                            {paymentMethod === 'boleto' && boletoInfo && (
                              <Row>
                                <Col lg={12} style={{ textAlign: 'center', padding: '16px' }}>
                                  {/* QR Code */}
                                  {boletoInfo.qrCode && (
                                    <>
                                      <p>Pague com o QR Code abaixo:</p>
                                      <img
                                        src={boletoInfo.qrCode}
                                        alt="QR Code Boleto"
                                        style={{
                                          margin: '16px auto',
                                          width: '200px',
                                          height: '200px',
                                          objectFit: 'contain',
                                          display: 'block',
                                        }}
                                      />
                                    </>
                                  )}
                                  {boletoInfo.qrCode && (boletoInfo.copyPaste || boletoInfo.pdfUrl) && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '24px 0',
                                      }}
                                    >
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                      <span style={{ margin: '0 10px', color: '#888' }}>ou</span>
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                    </div>
                                  )}
                                  {/* Código de barras */}
                                  <>
                                    <p>Pague com o código de barras:</p>
                                    <div
                                      style={{
                                        display: 'block',
                                        background: '#f9f9f9',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        margin: '12px auto',
                                        wordBreak: 'break-all',
                                        maxWidth: '100%',
                                      }}
                                    >
                                      {boletoInfo.copyPaste}
                                    </div>
                                  </>
                                  {/* Segundo divisor visual */}
                                  {boletoInfo.copyPaste && boletoInfo.pdfUrl && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '24px 0',
                                      }}
                                    >
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                      <span style={{ margin: '0 10px', color: '#888' }}>ou</span>
                                      <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
                                    </div>
                                  )}
                                  {/* Botão PDF */}
                                  {boletoInfo.pdfUrl && (
                                    <Button as="a" href={boletoInfo.pdfUrl} target="_blank" mt="8px">
                                      Baixar PDF do boleto
                                    </Button>
                                  )}
                                </Col>
                              </Row>
                            )}
                          </>
                        ) : (
                          <>
                            <MdErrorOutline size={80} color="red" />
                            <h2 style={{ marginTop: '20px' }}>{paymentMessage}</h2>
                            <Button mt="16px" onClick={() => setPaymentStatus(null)}>
                              Voltar
                            </Button>
                          </>
                        )}
                      </div>
                    </FormCard>
                  </Col>
                </Row>
              )}
              {!paymentStatus && paymentMethod === 'credit_card' && (
                <FormCard>
                  <FormContainer>
                    <Form onSubmit={formikCreditCard.handleSubmit}>
                      {paymentPlans.length > 0 && (
                        <Row>
                          <Col lg={12}>
                            <FormGroup mb="20px">
                              <Label name="installments" required>
                                Quantidade de parcelas
                              </Label>
                              <Select
                                name="installments"
                                id="installments"
                                {...formikCreditCard.getFieldProps('installments')}
                              >
                                <option value="">Selecione</option>
                                {paymentPlans.map(plan => (
                                  <option key={plan.number} value={plan.number}>
                                    {plan.number}x de {formatNumber.format(plan.amount)}
                                  </option>
                                ))}
                              </Select>
                              <FieldError
                                name="installments"
                                errors={formikCreditCard.errors}
                                touched={formikCreditCard.touched}
                                submitCount={formikCreditCard.submitCount}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="cardNumber" required>
                              Número do Cartão
                            </Label>
                            <InputMask
                              {...formikCreditCard.getFieldProps('cardNumber')}
                              mask="9999 9999 9999 9999"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input
                                  {...inputProps}
                                  type="text"
                                  name="cardNumber"
                                  placeholder="0000 0000 0000 0000"
                                />
                              )}
                            </InputMask>
                            <FieldError
                              name="cardNumber"
                              errors={formikCreditCard.errors}
                              touched={formikCreditCard.touched}
                              submitCount={formikCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="holderName" required>
                              Nome Impresso
                            </Label>
                            <Input
                              {...formikCreditCard.getFieldProps('holderName')}
                              type="text"
                              name="holderName"
                              placeholder="Nome como no cartão"
                            />
                            <FieldError
                              name="holderName"
                              errors={formikCreditCard.errors}
                              touched={formikCreditCard.touched}
                              submitCount={formikCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="expMonth" required>
                              Mês
                            </Label>
                            <InputMask {...formikCreditCard.getFieldProps('expMonth')} mask="99" maskChar={null}>
                              {inputProps => <Input {...inputProps} type="text" name="expMonth" placeholder="MM" />}
                            </InputMask>
                            <FieldError
                              name="expMonth"
                              errors={formikCreditCard.errors}
                              touched={formikCreditCard.touched}
                              submitCount={formikCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="expYear" required>
                              Ano
                            </Label>
                            <InputMask {...formikCreditCard.getFieldProps('expYear')} mask="9999" maskChar={null}>
                              {inputProps => <Input {...inputProps} type="text" name="expYear" placeholder="AAAA" />}
                            </InputMask>
                            <FieldError
                              name="expYear"
                              errors={formikCreditCard.errors}
                              touched={formikCreditCard.touched}
                              submitCount={formikCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="cvv" required>
                              CVV
                            </Label>
                            <InputMask {...formikCreditCard.getFieldProps('cvv')} mask="999" maskChar={null}>
                              {inputProps => <Input {...inputProps} type="text" name="cvv" placeholder="CVV" />}
                            </InputMask>
                            <FieldError
                              name="cvv"
                              errors={formikCreditCard.errors}
                              touched={formikCreditCard.touched}
                              submitCount={formikCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12}>
                          <SubmitButton isLoading={loading} mt="24px" fullwidth text="Pagar" type="submit" />
                        </Col>
                      </Row>
                    </Form>
                  </FormContainer>
                </FormCard>
              )}
              {!paymentStatus && paymentMethod === 'multi_credit_card' && (
                <FormCard>
                  <FormContainer>
                    <Form onSubmit={formikMultiCreditCard.handleSubmit}>
                      <h4>Cartão 1</h4>
                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card1Amount" required>
                              Valor no Cartão 1
                            </Label>
                            <NumericFormat
                              name="card1Amount"
                              thousandSeparator="."
                              decimalSeparator=","
                              prefix="R$ "
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              value={formikMultiCreditCard.values.card1Amount}
                              onValueChange={valuesObj => {
                                const { floatValue } = valuesObj;
                                const card1 = floatValue || 0;
                                const total = order.total_amount || 0;
                                const card2 = Math.max(total - card1, 0);
                                setCard1Amount(card1 || '');
                                setCard2Amount(card2 || '');

                                formikMultiCreditCard.setFieldValue('card1Amount', card1);
                                formikMultiCreditCard.setFieldValue('card2Amount', card2);
                                formikMultiCreditCard.setFieldValue('card1Installments', '');
                                formikMultiCreditCard.setFieldValue('card2Installments', '');
                              }}
                              customInput={Input}
                              placeholder="0,00"
                            />
                            <FieldError
                              name="card1Amount"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {paymentPlans.length > 0 && (
                        <Row>
                          <Col lg={12}>
                            <FormGroup mb="20px">
                              <Label name="card1Installments" required>
                                Quantidade de parcelas
                              </Label>
                              <Select
                                name="card1Installments"
                                id="card1Installments"
                                {...formikMultiCreditCard.getFieldProps('card1Installments')}
                              >
                                <option value="">Selecione</option>
                                {paymentPlans.map(plan => {
                                  const amountPerInstallment =
                                    card1Amount && !isNaN(parseFloat(card1Amount))
                                      ? (parseFloat(card1Amount) / plan.number).toFixed(2)
                                      : formatNumber.format(plan.amount);
                                  return (
                                    <option key={plan.number} value={plan.number}>
                                      {plan.number}x de R$ {amountPerInstallment}
                                    </option>
                                  );
                                })}
                              </Select>
                              <FieldError
                                name="card1Installments"
                                errors={formikMultiCreditCard.errors}
                                touched={formikMultiCreditCard.touched}
                                submitCount={formikMultiCreditCard.submitCount}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card1Number" required>
                              Número do Cartão
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card1Number')}
                              mask="9999 9999 9999 9999"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input
                                  {...inputProps}
                                  type="text"
                                  name="card1Number"
                                  placeholder="0000 0000 0000 0000"
                                />
                              )}
                            </InputMask>
                            <FieldError
                              name="card1Number"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card1HolderName" required>
                              Nome Impresso
                            </Label>
                            <Input
                              {...formikMultiCreditCard.getFieldProps('card1HolderName')}
                              type="text"
                              name="card1HolderName"
                              placeholder="Nome como no cartão"
                            />
                            <FieldError
                              name="card1HolderName"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card1ExpMonth" required>
                              Mês
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card1ExpMonth')}
                              mask="99"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input {...inputProps} type="text" name="card1ExpMonth" placeholder="MM" />
                              )}
                            </InputMask>
                            <FieldError
                              name="card1ExpMonth"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card1ExpYear" required>
                              Ano
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card1ExpYear')}
                              mask="9999"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input {...inputProps} type="text" name="card1ExpYear" placeholder="AAAA" />
                              )}
                            </InputMask>
                            <FieldError
                              name="card1ExpYear"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card1Cvv" required>
                              CVV
                            </Label>
                            <InputMask {...formikMultiCreditCard.getFieldProps('card1Cvv')} mask="999" maskChar={null}>
                              {inputProps => <Input {...inputProps} type="text" name="card1Cvv" placeholder="CVV" />}
                            </InputMask>
                            <FieldError
                              name="card1Cvv"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <h4>Cartão 2</h4>
                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card2Amount" required>
                              Valor no Cartão 2
                            </Label>
                            <NumericFormat
                              name="card2Amount"
                              thousandSeparator="."
                              decimalSeparator=","
                              prefix="R$ "
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              value={formikMultiCreditCard.values.card2Amount}
                              onValueChange={valuesObj => {
                                const { floatValue } = valuesObj;
                                const card2 = floatValue || 0;
                                const total = order.total_amount || 0;
                                const card1 = Math.max(total - card2, 0);
                                setCard1Amount(card1 || '');
                                setCard2Amount(card2 || '');

                                formikMultiCreditCard.setFieldValue('card2Amount', card2);
                                formikMultiCreditCard.setFieldValue('card1Amount', card1);
                                formikMultiCreditCard.setFieldValue('card1Installments', '');
                                formikMultiCreditCard.setFieldValue('card2Installments', '');
                              }}
                              customInput={Input}
                              placeholder="0,00"
                            />
                            <FieldError
                              name="card2Amount"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {paymentPlans.length > 0 && (
                        <Row>
                          <Col lg={12}>
                            <FormGroup mb="20px">
                              <Label name="card2Installments" required>
                                Quantidade de parcelas
                              </Label>
                              <Select
                                name="card2Installments"
                                id="card2Installments"
                                {...formikMultiCreditCard.getFieldProps('card2Installments')}
                              >
                                <option value="">Selecione</option>
                                {paymentPlans.map(plan => {
                                  const amountPerInstallment =
                                    card2Amount && !isNaN(parseFloat(card2Amount))
                                      ? (parseFloat(card2Amount) / plan.number).toFixed(2)
                                      : formatNumber.format(plan.amount);
                                  return (
                                    <option key={plan.number} value={plan.number}>
                                      {plan.number}x de R$ {amountPerInstallment}
                                    </option>
                                  );
                                })}
                              </Select>
                              <FieldError
                                name="card2Installments"
                                errors={formikMultiCreditCard.errors}
                                touched={formikMultiCreditCard.touched}
                                submitCount={formikMultiCreditCard.submitCount}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card2Number" required>
                              Número do Cartão
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card2Number')}
                              mask="9999 9999 9999 9999"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input
                                  {...inputProps}
                                  type="text"
                                  name="card2Number"
                                  placeholder="0000 0000 0000 0000"
                                />
                              )}
                            </InputMask>
                            <FieldError
                              name="card2Number"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12}>
                          <FormGroup mb="20px">
                            <Label name="card2HolderName" required>
                              Nome Impresso
                            </Label>
                            <Input
                              {...formikMultiCreditCard.getFieldProps('card2HolderName')}
                              type="text"
                              name="card2HolderName"
                              placeholder="Nome como no cartão"
                            />
                            <FieldError
                              name="card2HolderName"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card2ExpMonth" required>
                              Mês
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card2ExpMonth')}
                              mask="99"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input {...inputProps} type="text" name="card2ExpMonth" placeholder="MM" />
                              )}
                            </InputMask>
                            <FieldError
                              name="card2ExpMonth"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card2ExpYear" required>
                              Ano
                            </Label>
                            <InputMask
                              {...formikMultiCreditCard.getFieldProps('card2ExpYear')}
                              mask="9999"
                              maskChar={null}
                            >
                              {inputProps => (
                                <Input {...inputProps} type="text" name="card2ExpYear" placeholder="AAAA" />
                              )}
                            </InputMask>
                            <FieldError
                              name="card2ExpYear"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={4}>
                          <FormGroup mb="20px">
                            <Label name="card2Cvv" required>
                              CVV
                            </Label>
                            <InputMask {...formikMultiCreditCard.getFieldProps('card2Cvv')} mask="999" maskChar={null}>
                              {inputProps => <Input {...inputProps} type="text" name="card2Cvv" placeholder="CVV" />}
                            </InputMask>
                            <FieldError
                              name="card2Cvv"
                              errors={formikMultiCreditCard.errors}
                              touched={formikMultiCreditCard.touched}
                              submitCount={formikMultiCreditCard.submitCount}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={12}>
                          <SubmitButton isLoading={loading} mt="24px" fullwidth text="Pagar com 2 Cartões" />
                        </Col>
                      </Row>
                    </Form>
                  </FormContainer>
                </FormCard>
              )}
              {!paymentStatus && paymentMethod === 'pix' && (
                <FormCard>
                  <FormContainer>
                    <>
                      <Row>
                        <Col lg={12}>
                          <SubmitButton
                            isLoading={loading}
                            mt="24px"
                            fullwidth
                            text="Gerar PIX"
                            type="button"
                            onClick={handlePixPayment}
                          />
                        </Col>
                      </Row>
                    </>
                  </FormContainer>
                </FormCard>
              )}
              {!paymentStatus && paymentMethod === 'boleto' && (
                <FormCard>
                  <FormContainer>
                    <>
                      <Row>
                        <Col lg={12}>
                          <SubmitButton
                            isLoading={loading}
                            mt="24px"
                            fullwidth
                            text="Gerar Boleto"
                            type="button"
                            onClick={handleBoletoPayment}
                          />
                        </Col>
                      </Row>
                    </>
                  </FormContainer>
                </FormCard>
              )}
            </Col>
            <Col lg={4} md={12} sm={12}>
              <FormCard>
                <h4 style={{ textAlign: 'left', marginBottom: '16px' }}>Resumo do Pedido</h4>
                {order && order.invoices && order?.invoices?.length > 0 ? (
                  <>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {order.invoices.map((item, idx) => (
                        <li
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                            fontSize: '14px',
                          }}
                        >
                          <span>{item.title}</span>
                          <span>{formatNumber.format(item.amount)}</span>
                        </li>
                      ))}
                    </ul>
                    <hr style={{ margin: '16px 0' }} />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        fontSize: '16px',
                      }}
                    >
                      <span>Total</span>
                      <span>{formatNumber.format(order.total_amount)}</span>
                    </div>
                  </>
                ) : (
                  <p>Carregando pedido...</p>
                )}
              </FormCard>
            </Col>
          </>
        )}
        {!isLoadingOrder && order?.invoices?.length === 0 && <p>Pedido não encontrado.</p>}
      </Row>
    </CheckoutWrapper>
  );
}
